import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Textarea,
} from '@fluentui/react-components';
import { Add20Regular, ArrowDown16Regular, ArrowUp16Regular, Delete20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideField } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideRowActions, GuideTable, GuideTh, guideCellClassName as cell, guideRowClassName } from '../../../theme/components/GuideTable';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import { GuideSkeleton } from '../../../theme/components/GuideSkeleton';
import { GuideBannerAction } from '../../../theme/components/GuideButtons';
import { GuideCountChip } from '../../../theme/components/GuideKpiCard';
import { StatusPill } from '../../../theme/components/StatusPill';
import { ProtocolReferenceTemplateEditor, TemplateTestPanel } from '../ProviderConsumptionImport/ProtocolReferenceTemplateEditor';
import { getApiErrorMessage } from '../../../functions/ProviderConsumptionImport.Functions';
import { formatDateTime } from '../../../functions/ProviderConsumptions.Functions';
import { MarkLabel, moveItem, partsPreview, textsFromLines } from '../../../functions/ProtocolReferenceTemplate.Functions';
import {
  ProtocolReferenceTemplate,
  REFERENCE_TEMPLATE_MAX_ACTIVE,
  ReferenceTemplatePart,
  SaveReferenceTemplateRequest,
  TemplateTestResult,
} from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import '../ProviderConsumptionImport/ProviderConsumptionImport.Styles.css';

export interface ProviderReferenceTemplatesSectionProps {
  LoadTemplates: () => Promise<ProtocolReferenceTemplate[]>;
  CreateTemplate: (body: SaveReferenceTemplateRequest) => Promise<ProtocolReferenceTemplate | undefined>;
  UpdateTemplate: (templateId: string, body: SaveReferenceTemplateRequest) => Promise<ProtocolReferenceTemplate | undefined>;
  DeleteTemplate: (templateId: string) => Promise<void>;
  ReorderTemplates: (templateIds: string[]) => Promise<ProtocolReferenceTemplate[]>;
  TestTemplate: (parts: ReferenceTemplatePart[], texts: string[]) => Promise<TemplateTestResult[]>;
}

/** Editor abierto: alta (sin plantilla) o edición. */
type EditorState = { template?: ProtocolReferenceTemplate };

const saveBody = (template: ProtocolReferenceTemplate, active: boolean): SaveReferenceTemplateRequest => ({
  name: template.name,
  parts: template.parts,
  sampleText: template.sampleText ?? null,
  active,
});

/**
 * "Formatos de referencia" del proveedor (spec 009, US5, FR-018): las plantillas en orden de aplicación (activas e
 * inactivas), con subir / bajar (botones accesibles por teclado; el foco sigue a la fila), editar, activar / desactivar,
 * eliminar con confirmación y "Probar con un texto". Cambiarlas no toca consumos ya importados.
 */
export const ProviderReferenceTemplatesSection = (props: ProviderReferenceTemplatesSectionProps) => {
  const { t } = useTranslation();
  const tr = (key: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.templates.section.${key}`, options) as string;
  const trt = (key: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.templates.tryText.${key}`, options) as string;
  const fieldLabel = (label: MarkLabel) => t(`providerConsumptionImport.templates.fields.${label}`) as string;

  const [items, setItems] = useState<ProtocolReferenceTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(undefined);
  const [reloadToken, setReloadToken] = useState(0);
  const [busyId, setBusyId] = useState<string | undefined>(undefined);
  const [actionError, setActionError] = useState<{ title: string; error: unknown } | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [editor, setEditor] = useState<EditorState | undefined>(undefined);
  const [deleting, setDeleting] = useState<ProtocolReferenceTemplate | undefined>(undefined);
  const [removing, setRemoving] = useState(false);
  const [deleteError, setDeleteError] = useState<unknown>(undefined);
  const [trying, setTrying] = useState<ProtocolReferenceTemplate | undefined>(undefined);
  const [tryValue, setTryValue] = useState('');
  const [tryResults, setTryResults] = useState<TemplateTestResult[] | undefined>(undefined);
  const [tryRunning, setTryRunning] = useState(false);
  const [tryError, setTryError] = useState<unknown>(undefined);
  const requestId = useRef(0);
  /** Botón que recupera el foco después de mover una fila (la tabla se vuelve a dibujar). */
  const focusAfterMove = useRef<{ id: string; direction: 'up' | 'down' } | undefined>(undefined);
  const buttons = useRef<Record<string, HTMLButtonElement | null>>({});

  const loadTemplates = useRef(props.LoadTemplates);
  loadTemplates.current = props.LoadTemplates;

  const load = useCallback(() => {
    const id = ++requestId.current;
    setLoading(true);
    setLoadError(undefined);
    loadTemplates
      .current()
      .then((list) => {
        if (id === requestId.current) setItems(list);
      })
      .catch((error: unknown) => {
        if (id === requestId.current) setLoadError(error);
      })
      .finally(() => {
        if (id === requestId.current) {
          setLoading(false);
          setLoaded(true);
        }
      });
  }, []);

  useEffect(() => {
    load();
  }, [load, reloadToken]);

  // Después de mover (y de rehabilitar los botones), el foco vuelve al mismo botón de la fila movida.
  useEffect(() => {
    const target = focusAfterMove.current;
    if (!target || busyId) return;
    focusAfterMove.current = undefined;
    const same = buttons.current[`${target.direction}-${target.id}`];
    const other = buttons.current[`${target.direction === 'up' ? 'down' : 'up'}-${target.id}`];
    (same && !same.disabled ? same : other)?.focus();
  }, [items, busyId]);

  const activeCount = items.filter((item) => item.active).length;

  const move = async (index: number, direction: 'up' | 'down') => {
    const template = items[index];
    const next = moveItem(items, index, direction === 'up' ? -1 : 1);
    if (next === items) return;
    setBusyId(template.id);
    setActionError(undefined);
    try {
      const saved = await props.ReorderTemplates(next.map((item) => item.id));
      focusAfterMove.current = { id: template.id, direction };
      const list = saved.length > 0 ? saved : next;
      setItems(list);
      setNotice(tr('moved', { name: template.name, position: list.findIndex((item) => item.id === template.id) + 1 }));
    } catch (error: unknown) {
      setActionError({ title: tr('reorderError'), error });
    } finally {
      setBusyId(undefined);
    }
  };

  const toggleActive = async (template: ProtocolReferenceTemplate) => {
    setBusyId(template.id);
    setActionError(undefined);
    try {
      const updated = await props.UpdateTemplate(template.id, saveBody(template, !template.active));
      setItems((current) => current.map((item) => (item.id === template.id ? { ...item, ...(updated ?? { active: !template.active }) } : item)));
      setNotice(tr(template.active ? 'deactivated' : 'activated', { name: template.name }));
    } catch (error: unknown) {
      setActionError({ title: tr('actionError'), error });
    } finally {
      setBusyId(undefined);
    }
  };

  const remove = async () => {
    const template = deleting;
    if (!template) return;
    setRemoving(true);
    setDeleteError(undefined);
    try {
      await props.DeleteTemplate(template.id);
      setDeleting(undefined);
      setItems((current) => current.filter((item) => item.id !== template.id));
      setNotice(tr('deleted', { name: template.name }));
    } catch (error: unknown) {
      setDeleteError(error);
    } finally {
      setRemoving(false);
    }
  };

  const runTry = async () => {
    if (!trying) return;
    const texts = textsFromLines(tryValue);
    if (texts.length === 0) return;
    setTryRunning(true);
    setTryError(undefined);
    try {
      setTryResults(await props.TestTemplate(trying.parts, texts));
    } catch (error: unknown) {
      setTryError(error);
    } finally {
      setTryRunning(false);
    }
  };

  const openTry = (template: ProtocolReferenceTemplate) => {
    setTrying(template);
    setTryValue(template.sampleText ?? '');
    setTryResults(undefined);
    setTryError(undefined);
  };

  const renderBody = () => {
    if (loadError !== undefined && items.length === 0) {
      return (
        <GuideMessageBar intent="error" layout="multiline" data-testid="prt-load-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('loadError')}</MessageBarTitle>
            {getApiErrorMessage(loadError)}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={() => setReloadToken((value) => value + 1)}>{tr('retry')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      );
    }
    if (!loaded || (loading && items.length === 0)) return <GuideSkeleton rows={3} label={tr('loading')} data-testid="prt-loading" />;
    if (items.length === 0) return <GuideEmptyState title={tr('emptyTitle')} text={tr('emptyText')} data-testid="prt-empty" />;
    return (
      <GuideTable ariaLabel={tr('tableLabel')} data-testid="prt-table">
        <thead>
          <tr>
            <GuideTh>{tr('columns.order')}</GuideTh>
            <GuideTh>{tr('columns.name')}</GuideTh>
            <GuideTh>{tr('columns.format')}</GuideTh>
            <GuideTh>{tr('columns.status')}</GuideTh>
            <GuideTh>{tr('columns.modified')}</GuideTh>
            <th className={cell('actions')} scope="col">
              <span className="pci-sr">{tr('columns.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody aria-busy={loading || undefined}>
          {items.map((template, index) => {
            const busy = busyId === template.id;
            return (
              <tr key={template.id} className={guideRowClassName({ inactive: !template.active })} data-testid={`prt-row-${template.id}`}>
                <td>
                  <span className="pci-template-order">
                    <span className="ichtys-num">{index + 1}</span>
                    <Button
                      ref={(element) => {
                        buttons.current[`up-${template.id}`] = element as HTMLButtonElement | null;
                      }}
                      size="small"
                      appearance="subtle"
                      icon={<ArrowUp16Regular />}
                      aria-label={tr('moveUp', { name: template.name })}
                      title={tr('moveUp', { name: template.name })}
                      disabled={index === 0 || !!busyId}
                      onClick={() => move(index, 'up')}
                      data-testid={`prt-up-${template.id}`}
                    />
                    <Button
                      ref={(element) => {
                        buttons.current[`down-${template.id}`] = element as HTMLButtonElement | null;
                      }}
                      size="small"
                      appearance="subtle"
                      icon={<ArrowDown16Regular />}
                      aria-label={tr('moveDown', { name: template.name })}
                      title={tr('moveDown', { name: template.name })}
                      disabled={index === items.length - 1 || !!busyId}
                      onClick={() => move(index, 'down')}
                      data-testid={`prt-down-${template.id}`}
                    />
                  </span>
                </td>
                <td>
                  <span className="pci-review-name">{template.name}</span>
                  {template.sampleText && <span className="pci-secondary">{template.sampleText}</span>}
                </td>
                <td>
                  <span className="pci-template-format">{partsPreview(template.parts, fieldLabel)}</span>
                </td>
                <td>
                  <StatusPill
                    tone={template.active ? 'success' : 'neutral'}
                    symbol={template.active ? '●' : '○'}
                    className="pci-review-pill"
                    data-testid={`prt-status-${template.id}`}
                  >
                    {template.active ? tr('active') : tr('inactive')}
                  </StatusPill>
                </td>
                <td className="ichtys-num pci-nowrap">{formatDateTime(template.lastModified)}</td>
                <td className={cell('actions')} data-testid={`prt-actions-${template.id}`}>
                  <GuideRowActions
                    label={tr('rowActions', { name: template.name })}
                    busy={busy}
                    disabled={!!busyId}
                    actions={[
                      { key: 'edit', label: tr('edit'), onClick: () => setEditor({ template }) },
                      { key: 'toggle', label: template.active ? tr('deactivate') : tr('activate'), onClick: () => toggleActive(template) },
                      { key: 'try', label: tr('tryText'), onClick: () => openTry(template) },
                      {
                        key: 'delete',
                        label: tr('delete'),
                        danger: true,
                        onClick: () => {
                          setDeleteError(undefined);
                          setDeleting(template);
                        },
                      },
                    ]}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </GuideTable>
    );
  };

  return (
    <GuideDrawerSection title={tr('title')} description={tr('description')} testId="prt-section">
      <div className="pci-review-filters">
        <Button appearance="secondary" icon={<Add20Regular />} onClick={() => setEditor({})} data-testid="prt-add">
          {tr('add')}
        </Button>
        {loaded && <GuideCountChip label={tr('activeLabel')} count={`${activeCount} / ${REFERENCE_TEMPLATE_MAX_ACTIVE}`} />}
      </div>
      <div className="pci-review-apply-status" aria-live="polite" data-testid="prt-notice">
        {notice ?? ''}
      </div>
      {actionError && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="prt-action-error">
          <MessageBarBody>
            <MessageBarTitle>{actionError.title}</MessageBarTitle>
            {getApiErrorMessage(actionError.error)}
          </MessageBarBody>
        </GuideMessageBar>
      )}
      {loadError !== undefined && items.length > 0 && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="prt-load-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('loadError')}</MessageBarTitle>
            {getApiErrorMessage(loadError)}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={() => setReloadToken((value) => value + 1)}>{tr('retry')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}
      {renderBody()}

      <ProtocolReferenceTemplateEditor
        open={!!editor}
        template={editor?.template}
        sampleText=""
        testTexts={[]}
        onTest={props.TestTemplate}
        onSave={(body, templateId) => (templateId ? props.UpdateTemplate(templateId, body) : props.CreateTemplate(body))}
        onDismiss={() => setEditor(undefined)}
        onSaved={(_template, name) => {
          setEditor(undefined);
          setNotice(tr('saved', { name }));
          setReloadToken((value) => value + 1);
        }}
      />

      <Dialog open={!!deleting} modalType="alert" onOpenChange={(_event, data) => !data.open && !removing && setDeleting(undefined)}>
        <DialogSurface data-testid="prt-delete-dialog">
          <DialogBody>
            <DialogTitle>{tr('deleteTitle')}</DialogTitle>
            <DialogContent>
              <div className="pci-stack">
                <p>{tr('deleteText', { name: deleting?.name ?? '' })}</p>
                {deleteError !== undefined && (
                  <GuideMessageBar intent="error" layout="multiline" data-testid="prt-delete-error">
                    <MessageBarBody>
                      <MessageBarTitle>{tr('deleteError')}</MessageBarTitle>
                      {getApiErrorMessage(deleteError)}
                    </MessageBarBody>
                  </GuideMessageBar>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleting(undefined)} disabled={removing} data-testid="prt-delete-cancel">
                {tr('cancel')}
              </Button>
              <Button
                appearance="secondary"
                className="pci-danger"
                icon={<Delete20Regular />}
                onClick={remove}
                disabled={removing}
                aria-busy={removing || undefined}
                data-testid="prt-delete-confirm"
              >
                {removing ? tr('deleting') : tr('deleteConfirm')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={!!trying} modalType="modal" onOpenChange={(_event, data) => !data.open && setTrying(undefined)}>
        <DialogSurface className="pci-template-editor" data-testid="prt-try-dialog">
          <DialogBody>
            <DialogTitle>{trt('title', { name: trying?.name ?? '' })}</DialogTitle>
            <DialogContent>
              <div className="pci-stack">
                <span className="pci-template-format">{trying ? partsPreview(trying.parts, fieldLabel) : ''}</span>
                <GuideField label={trt('label')} hint={trt('hint')}>
                  <Textarea value={tryValue} resize="vertical" onChange={(_event, data) => setTryValue(data.value)} data-testid="prt-try-text" />
                </GuideField>
                {tryError !== undefined && (
                  <GuideMessageBar intent="error" layout="multiline" data-testid="prt-try-error">
                    <MessageBarBody>
                      <MessageBarTitle>{trt('error')}</MessageBarTitle>
                      {getApiErrorMessage(tryError)}
                    </MessageBarBody>
                  </GuideMessageBar>
                )}
                {tryResults && <TemplateTestPanel results={tryResults} loading={tryRunning} />}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setTrying(undefined)} data-testid="prt-try-close">
                {trt('close')}
              </Button>
              <Button
                appearance="primary"
                onClick={runTry}
                disabled={tryRunning || textsFromLines(tryValue).length === 0}
                aria-busy={tryRunning || undefined}
                data-testid="prt-try-run"
              >
                {tryRunning ? trt('running') : trt('run')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </GuideDrawerSection>
  );
};

export default ProviderReferenceTemplatesSection;
